<template>
  <div>
    <div
      id="app"
      :class="{
        light: useLightUi,
        'extra-light': isLogin || isOnMarketplace,
        'white-bg': showWhiteBg,
      }"
    >
      <div
        class="all-headers-wrapper"
        :class="{
          light: useLightUi,
          'white-bg': showWhiteBg || isPasswordPage,
        }"
      >
        <acv-header-feedback
          v-if="!fromInitialization"
          :show-auction-access-banner="showBanner"
          :show-mobile-announcements-carousel="showMobileAnnouncementsCarousel"
        />
        <component
          :is="header"
          v-if="showHeader && !fromInitialization"
          id="acvHeader"
          :back-button-route="computedBackButtonRoute"
          :is-authorized="hasAccess"
          :current-user="currentUser"
          :dealership-name="dealershipName"
          :has-access="hasAccess"
          :show-filter-icon="showFilterIcon"
          :show-back-button="showBackButton"
          :can-sell="canSell"
          :number-of-active-filters="numberOfActiveFilters"
          :show-buttons="showRegistrationHeaderButtons"
        />
      </div>

      <div
        v-if="isLogin"
        class="half-white tw-bg-white"
      />
      <acv-visibility-change @visibility-changed="handleVisibilityChange" />

      <router-view
        class="main-content"
        :class="{
          'default-view': showHeader && !isLogin,
          'authentication-view': isLogin || isAuctionAccessPage,
          'simplified-view': isAuthorized && simplifiedHeader,
          'mobile-view__tablet': isAuthorized && isTabletExperience,
          'mobile-view__desktop': isAuthorized && !isTabletExperience
            && !isPhoneExperience,
          'marketplace-view': isOnMarketplace,
          'with-announcements': hasAnyAnnouncements,
        }"
        :with-announcements="hasAnyAnnouncements"
      />
      <acv-footer
        v-if="showFooter"
        id="acvFooter"
        :is-authorized="isAuthorized"
      />

      <acv-authentication-footer
        v-if="showAuthenticationFooter"
        :is-login="isLogin"
      />

      <component
        :is="bottomNavigationComponent"
        id="acvMobileNav"
        :route-name="routeName"
      />

      <acv-toast-message-list
        v-model="toastMessages"
        class="toast-message-list"
        @toast-message-linked-clicked="handleToastMessageLinkClicked"
      />

      <acv-unsupported-browser-alert
        v-if="unsupportedBrowserDetected"
        :show="unsupportedBrowserDetected && !unsupportedBrowserMessageDismissed"
        @dismissed="unsupportedBrowserMessageDismissed = true"
      />
      <acv-offline-alert
        v-if="showOfflineAlert"
      />
      <acv-dev-tools-panel
        v-if="includeDevToolsPanel"
      />
      <acv-app-launch-message
        v-if="showAppLaunchMessage"
        :app-launch-message="appLaunchMessage"
        @hide="clearAppLaunchMessage"
      />
      <acv-auction-common-modals
        v-if="allowAuctionCommonModals"
      />
      <acv-terms-of-service-modal
        v-if="tosEnabled"
        id="terms-of-service"
      />
    </div>
    <acv-dialog
      ref="rewardsPageDeniedErrorDialog"
      class="tw-text-text-secondary tw-text-base tw-font-regular rewards-dialog"
      title="Sorry, this program is not available for your dealership"
      submit-text="Ok"
      cancel-text=""
      cancel-disable
      size="large"
      :has-close="false"
      @submitted="$refs.rewardsPageDeniedErrorDialog.hide()"
    >
      <template #body>
        <div>
          <p>
            Email <a
              class="tw-text-text-link-on-light"
              href="mailto:dealerrewards@acvauctions.com"
            >
              dealerrewards@acvauctions.com
            </a>to explore options.
          </p>
        </div>
      </template>
    </acv-dialog>
  </div>
</template>

<script>
import { defineAsyncComponent, defineComponent, nextTick } from 'vue';
import get from 'lodash.get';
import { mapGetters, mapState, mapActions } from 'vuex';
import {
  AcvBanner, AcvVisibilityChange, AcvToastMessageList, AcvDialog,
} from '@acv/shared-vuejs';
import AcvFooter from '@/components/AcvFooter.vue';
import AcvHeader from '@/components/Header.vue';
import AcvSimplifiedHeader from '@/components/SimplifiedHeader.vue';
import AcvSearchHeader from '@/components/Search/SearchHeader.vue';
import Analytics from '@/mixins/Analytics';
import BrowserDetection from '@/mixins/BrowserDetection';
import DevFlags from '@/mixins/DevFlags';
import FeatureFlags from '@/mixins/FeatureFlags';
import RegisterServiceWorker from '@/mixins/RegisterServiceWorker';
import AcvOfflineAlert from '@/components/OfflineAlert.vue';
import { START_IDLE_TIMER, CLEAR_IDLE_TIMER } from '@/store/modules/network/network.type';
import Logout from '@/mixins/Logout';
import MobileTopBarHelpers from '@/mixins/MobileTopBarHelpers';
import {
  isLocked,
} from '@/utilities/JwtRefreshTokensLock';
import { datadogRum } from '@datadog/browser-rum';
import AcvHeaderFeedback from '@/components/AcvHeaderFeedback.vue';
import AcvTermsOfServiceModal from '@/components/TermsOfService/TermsOfServiceModal.vue';
import AcvMobileAnnouncementsCarousel from '@/components/MobileAnnouncementsCarousel.vue';
import useSalesforceEnhancedChat from '@/composables/useSalesforceEnhancedChat';
import useWindowSize from '@/composables/useWindowSize';
import useInactiveDealerWarning from '@/composables/useInactiveDealerWarning';

import { isWithinStockwave, iFrameParentDocumentUrl, isInIframe } from '@/utilities/iFrameDetection';
import eventBus from '@/utilities/EventBus';
import { markPendoReady } from '@/utilities/resolvers/pendoReadyResolver';
import { routeNames } from '@/constants/routes/acvRoute';
import ANNOUNCEMENT_EXCLUDED_ROUTE_KEYS from '@/constants/routes/announcementExcludedRoutes';

export default defineComponent({
  name: 'App',
  components: {
    AcvHeaderFeedback,
    AcvDialog,
    AuctionAccessBanner: defineAsyncComponent(() => import('@/components/AuctionAccess/AuctionAccessBanner.vue')),
    AcvBanner,
    AcvMobileAnnouncementsCarousel,
    AcvDevToolsPanel: defineAsyncComponent(() => import('@/components/DevToolsPanel.vue')),
    AcvSimplifiedHeader,
    AcvFooter,
    AcvHeader,
    AuctionAccessDialog: defineAsyncComponent(() => import('@/components/AuctionAccess/AuctionAccessDialog.vue')),
    AcvSearchHeader,
    AcvTermsOfServiceModal,
    AcvAuthenticationHeader: defineAsyncComponent(() => import('@/components/AcvAuthenticationHeader.vue')),
    AcvAuthenticationFooter: defineAsyncComponent(() => import('@/components/AcvAuthenticationFooter.vue')),
    AcvSearchBottomNavigation: defineAsyncComponent(() => import('@/components/Search/SearchBottomNavigation.vue')),
    AcvToastMessageList,
    AcvUnsupportedBrowserAlert: defineAsyncComponent(() => import('@/components/UnsupportedBrowserAlert.vue')),
    AcvOfflineAlert,
    AcvAppLaunchMessage: defineAsyncComponent(() => import('@/components/Modals/AppLaunchMessage.vue')),
    AcvMobileTopBar: defineAsyncComponent(() => import('@/components/MobileTopBar.vue')),
    AcvVisibilityChange,
    AcvAuctionCommonModals: defineAsyncComponent(() => import('@/components/Auction/AuctionCommonModals.vue')),
    AcvRegistrationHeader: defineAsyncComponent(() => import('@/components/AuctionAccess/AcvRegistrationHeader.vue')),
  },
  mixins: [
    Analytics,
    BrowserDetection,
    DevFlags,
    FeatureFlags,
    Logout,
    MobileTopBarHelpers,
    RegisterServiceWorker,
  ],
  beforeRouteLeave() {
    if (this.mobileMenuOpened) {
      document.body.classList.remove('no-scroll');
    }
  },
  setup() {
    return {
      ...useWindowSize(),
      ...useInactiveDealerWarning(),
    };
  },
  data: () => ({
    unsupportedBrowserMessageDismissed: false,
    toastMessages: [],
    pendingToastReinsertTimers: {},
    dealerActivatedMessageId: 'dealerActivatedMessageId',
    troublePullingTermsId: 'troublePullingTermsId',
    termsOfServiceAcceptedId: 'termsOfServiceAcceptedId',
    loggedInCheckInterval: null,
    breadcrumbBackRoute: '',
    launchMessageInterval: null,
    pendoReadyInterval: null,
    canShowLaunchMessage: true,
    auctionAccessBannerMsg: '',
    auctionAccessBannerLeadingMsg: '',
  }),
  computed: {
    ...mapGetters({
      isAuthorized: 'isAuthorized',
      currentUser: 'currentUser',
      isLimitedUser: 'isLimitedUser',
      clockOffset: 'auctions/clockOffset',
      isPubnubOnline: 'isPubnubOnline',
      activeFilters: 'filters/activeFilters',
      mobileMenuOpened: 'mobileTopBar/mobileMenuOpened',
      newTosAcceptanceRequired: 'newTosAcceptanceRequired',
      newTosContentUrl: 'newTosContentUrl',
      getTosErrors: 'getTosErrors',
      getTermsOfServiceAccepted: 'getTermsOfServiceAccepted',
      getTosSuccessToastShown: 'getTosSuccessToastShown',
      currentTosCheckInProgress: 'currentTosCheckInProgress',
      aaAllIssues: 'aaAllIssues',
      aaPersonIssues: 'aaPersonIssues',
      aaDealerIssues: 'aaDealerIssues',
      auctionAccessId: 'auctionAccessId',
      rewardsPageViewDenied: 'rewardsPageViewDenied',
      hasMobileAnnouncements: 'announcements/hasMobileAnnouncements',
      hasDesktopAnnouncements: 'announcements/hasDesktopAnnouncements',
      useNewVdpPreference: 'pandaMfe/useNewVdpPreference',
    }),
    ...mapState({
      appLaunchMessage: (state) => state.notifications.appLaunchMessage,
      tosPrintContent: (state) => state.termsOfService.tosPrintContent,
    }),
    isNewRegistrationForm() {
      const { meta } = this.$route;
      return Boolean(meta.isRegistrationV2) && !meta.useSplitHero;
    },
    showWhiteBg() {
      const nonWhitePages = [
        'MarketReport',
        'auction',
        'saved',
        'SaleEvent',
      ].includes(this.$route.name);

      if (nonWhitePages) return false;

      return (this.isPasswordPage
        || this.isAuthorized
        || this.isNewRegistrationForm)
        && !this.isLogin;
    },
    useVdp3HeaderAndBottomNav() {
      // vdp 3 as full handles its own header and hides the bottom nav
      // vdp 3 on srp covers over the web header and bottom nav
      return this.$route.name === 'auction'
        && this.featureFlags[this.featureFlagSlugs.usePandaMfe]
        && this.useNewVdpPreference;
    },
    header() {
      if (this.isLogout) {
        return undefined;
      }

      if (this.isLogin || this.isPasswordPage) {
        return 'AcvAuthenticationHeader';
      }

      if (this.isAuctionAccessPage) {
        return 'AcvRegistrationHeader';
      }

      if (this.isAuthorized) {
        if (this.isMobileExperience && this.useVdp3HeaderAndBottomNav) {
          return undefined;
        }

        // show top bar on phone and tablet if route allows for it or mobile menu open
        if (this.isMobileExperience
            && (this.$route.meta.hasMobileTopBarHeader || this.mobileMenuOpened)
        ) {
          return this.hasMobileTopBarContent
            ? 'AcvMobileTopBar'
            : undefined;
        }

        // Digital Clerks Titles Page
        if (this.simplifiedHeader) {
          return 'AcvSimplifiedHeader';
        }

        // no header on tablet
        if (this.isTabletExperience && !this.useLightUi) {
          return undefined;
        }

        // show search header only when not on a phone
        if (!this.isPhoneExperience) {
          return 'AcvSearchHeader';
        }
      }

      // show legacy header by default
      return 'AcvHeader';
    },
    bottomNavigationComponent() {
      // native apps render their own bottom navigation
      if (window.acvNative || this.useVdp3HeaderAndBottomNav) {
        return undefined;
      }

      if (!this.isLogout
          && (this.isMobileSearchExperience
              || (this.isMobileExperience && this.showMobileViewNavigation))
      ) {
        return 'AcvSearchBottomNavigation';
      }

      return undefined;
    },
    showBanner() {
      return this.hasAuctionAccessIssues
        && !this.isMobileExperience
        && !this.excludeAnnouncementsFromPage
        && !this.mobileMenuOpened;
    },
    useLightUi() {
      return this.$route.meta.useLightUi;
    },
    simplifiedHeader() {
      return this.$route.meta.simplifiedHeader;
    },
    isPhoneExperience() {
      return this.windowWidth < this.$tailwindHelper.getCurrentBreakpointByKey('tw-md').value;
    },
    isTabletExperience() {
      return this.windowWidth >= this.$tailwindHelper.getCurrentBreakpointByKey('tw-md').value
          && this.windowWidth < this.$tailwindHelper.getCurrentBreakpointByKey('tw-lg').value;
    },
    isMobileExperience() {
      return this.isTabletExperience || this.isPhoneExperience;
    },
    isMobileSearchExperience() {
      return this.isOnMarketplace && this.isMobileExperience;
    },
    isOnMarketplace() {
      return this.$route.name === 'Marketplace';
    },
    isLogin() {
      return this.$route.path.includes('/login');
    },
    isPasswordPage() {
      const routePath = this.$route.path;
      return routePath.includes('/forgot') || routePath.includes('/password');
    },
    isAuctionAccessPage() {
      const { path, meta } = this.$route;
      return path.includes('/registration') && !meta.isRegistrationV2;
    },
    isLogout() {
      return this.$route.path.includes('/logout');
    },
    routeName() {
      return get(this, '$route.name', '') || '';
    },
    numberOfActiveFilters() {
      return this.activeFilters.length;
    },
    includeDevToolsPanel() {
      return import.meta.env.VITE_ENV_NAME === 'development'
          || this.featureFlags[this.featureFlagSlugs.enableDevtoolsWeb];
    },
    enableStockwaveIntegration() {
      return this.featureFlags[this.featureFlagSlugs.enableStockwaveIntegration];
    },
    showAppLaunchMessage() {
      return this.appLaunchMessage
          && this.canShowLaunchMessage
          && this.hasAccess
          && !this.fromInitialization;
    },
    showAuthenticationFooter() {
      const unauthorizedAndNotRegistering = !this.isAuthorized && !this.fromInitialization;
      const isAuthorizedAndRegistered = this.isAuthorized && !this.useLightUi
          && !this.fromInitialization;
      const excludeFooter = this.$route.meta.isRegistrationV2 || false;
      /* eslint-disable no-mixed-operators */
      return ((unauthorizedAndNotRegistering) || (isAuthorizedAndRegistered)
          && (this.isLogin)) && !this.isSsoPage && !excludeFooter;
    },
    computedBackButtonRoute() {
      if (this.$route.path.includes('/myacv/')) {
        return '/myacv';
      }
      return get(this, 'breadcrumbBackRoute', '/auctions/active');
    },
    aaDialogTitle() {
      const allIssues = this.aaAllIssues || [];

      if (allIssues.length > 0) {
        return `AuctionACCESS Issue${allIssues.length > 1 ? 's' : ''}`;
      }
      return '';
    },
    hasAuctionAccessIssues() {
      const issues = this.aaAllIssues ?? [];
      return this.isAuthorized && issues.length > 0;
    },
    fromInitialization() {
      return get(this, '$route.path') === '/';
    },
    canSell() {
      return this.$store.getters.canSell;
    },
    showBackButton() {
      return this.$route.meta.showBackButton;
    },
    hasAccess() {
      return this.isAuthorized
          && !this.$route.path.includes('/registration')
          && !this.$route.meta.isRegistrationV2;
    },
    isSsoPage() {
      return this.$route?.meta?.ssoRoute || false;
    },
    showFilterIcon() {
      return this.$route.meta.showFilterIcon
          || (this.$route.name === 'BuyingRunList');
    },
    showHeader() {
      if (window.acvNative) {
        return false;
      }
      return get(this, '$route.meta.showHeader', true);
    },
    showFooter() {
      if (window.acvNative || this.useVdp3HeaderAndBottomNav) {
        return false;
      }
      const showFooter = get(this, '$route.meta.showFooter', true);
      return showFooter
          && !this.fromInitialization
          && !this.useLightUi;
    },
    showRegistrationHeaderButtons() {
      return get(this, '$route.meta.showRegistrationHeaderButtons', true);
    },
    showMobileAnnouncementsCarousel() {
      return !this.excludeAnnouncementsFromPage
        && !this.mobileMenuOpened
        && this.isAuthorized
        && this.isMobileExperience;
    },
    excludeAnnouncementsFromPage() {
      const excludedRoutes = ANNOUNCEMENT_EXCLUDED_ROUTE_KEYS.map((key) => routeNames.get(key));
      return excludedRoutes.includes(this.$route.name);
    },
    hasAnyAnnouncements() {
      return this.isMobileExperience
        ? this.hasMobileAnnouncements
        : this.hasDesktopAnnouncements;
    },
    showMobileViewNavigation() {
      return (this.showFooter || this.$route.meta.hideFooterOverride)
          && !this.fromInitialization
          && this.hasAccess;
    },
    showOfflineAlert() {
      if (!this.isPubnubOnline) {
        this.track(this.eventNames.noNetworkDialogShown);
      }
      return !this.isPubnubOnline;
    },
    dealershipName() {
      if (this.currentUser.hasMultipleDealerships) {
        return 'Multi-Dealer Account';
      }

      return this.currentUser.dealershipName;
    },

    pendoInitData() {
      let visitorId;
      let accountId;
      let dealerIds;
      if (import.meta.env.VITE_ENV_NAME === 'production') {
        visitorId = `${this.currentUser.user_id}`;
        accountId = `${this.currentUser.dealer_id}`;
      } else {
        // De-duplicate IDs across environments by adding a suffix in non-prod
        visitorId = `${this.currentUser.user_id}-${import.meta.env.VITE_ENV_NAME}`;
        accountId = `${this.currentUser.dealer_id}-${import.meta.env.VITE_ENV_NAME}`;
      }
      if (typeof this.currentUser.activeDealerships === 'object' && this.currentUser.activeDealerships !== null) {
        dealerIds = Object.keys(this.currentUser.activeDealerships);
      }
      return {
        visitor: {
          id: visitorId,
          email: this.currentUser.email,
          full_name: this.currentUser.full_name,
          active_dealers: dealerIds,
          role: null,
          creationDate: null,
        },
        account: {
          id: accountId,
          name: this.currentUser.dealership_name,
          is_paying: null,
          monthly_value: null,
          planLevel: null,
        },
      };
    },
    allowAuctionCommonModals() {
      return this.$route.meta.allowAuctionCommonModals;
    },
    clockDrifted() {
      return (this.clockOffset > 5
          && this.featureFlags[this.featureFlagSlugs.enableClockDriftTracking]
      );
    },
    tosEnabled() {
      if (!this.isLogout && this.currentUser.user_id !== undefined) {
        return this.featureFlags[this.featureFlagSlugs.enableTosAcceptance]
            && this.hasAccess
            && this.newTosAcceptanceRequired
            && !!this.newTosContentUrl
            && !this.getTermsOfServiceAccepted
            && !this.excludeAnnouncementsFromPage;
      }
      return false;
    },
  },
  watch: {
    rewardsPageViewDenied: {
      handler(newValue) {
        if (newValue) {
          this.$refs.rewardsPageDeniedErrorDialog.show();
          this.$store.dispatch('setRewardsPageViewDenied', false);
        }
      },
      immediate: true,
    },
    async isAuthorized() {
      if (this.isAuthorized) {
        this.setupCustomAnalyticsAttributes();
        this.initializeApplication();

        // Initialize Salesforce Enhanced Chat when user becomes authorized
        nextTick(() => {
          useSalesforceEnhancedChat(this.$route);
        });
      }

      if (!this.isAuthorized) {
        await this.$store.dispatch('disableMessaging');

        // Call teardown just in case the user logs in with different credentials
        if (typeof pendo !== 'undefined') {
          pendo.teardown();
        }
      }
    },
    getTosErrors: {
      handler(newValue, oldValue) {
        if (newValue !== undefined
            && oldValue !== undefined
            && newValue.length !== oldValue.length
            && newValue.length > 0
        ) {
          this.addTroublePullingTermsMessage();
        }
      },
      deep: true,
      immediate: true,
    },
    getTermsOfServiceAccepted: {
      handler(newValue, oldValue) {
        if (newValue !== oldValue
            && newValue === true
            && !this.getTosSuccessToastShown) {
          this.addTermsOfServiceAcceptedSuccessfullyMessage();
          this.setTosSuccessToastShown(true);
        }
      },
      immediate: true,
    },
    isLimitedUser(isLimitedUser, wasIsLimitedUser) {
      this.clearToastMessageById(this.dealerActivatedMessageId);
      this.syncInactiveDealerWarning();
      if (!this.isLimitedUser
          && isLimitedUser === false
          && wasIsLimitedUser === true
          && this.isAuthorized) {
        this.addDealerActivatedMessage();
      }
    },
    '$route.name': {
      handler(newName, oldName) {
        this.syncInactiveDealerWarning();
        // on route change, if there was any lingering app launch message,
        // show them else remain hiding them
        if (newName && oldName && !['login', 'logout'].includes(oldName)) {
          if (this.appLaunchMessage) {
            this.handleAppLaunchMessageVisibility(true);
          } else {
            this.handleAppLaunchMessageVisibility(false);
          }
        }

        // remove mobile top bar when route changes
        if (newName !== oldName) {
          this.teardownMobileTopBar();
        }

        // Initialize Salesforce Enhanced Chat when route changes
        if (this.isAuthorized) {
          nextTick(() => {
            useSalesforceEnhancedChat(this.$route);
          });
        }
      },
    },
    mobileMenuOpened(newVal) {
      if (this.isMobileExperience) {
        if (newVal) {
          document.body.classList.add('no-scroll');
        } else {
          document.body.classList.remove('no-scroll');
        }
      }
    },
    isMobileExperience(newValue, oldValue) {
      if (!newValue && oldValue
          && document.body.classList.contains('no-scroll')
      ) {
        document.body.classList.remove('no-scroll');
      }
    },
    enableStockwaveIntegration: {
      immediate: true,
      async handler(newVal, oldVal) {
        if (newVal) {
          let stockwaveDevMode = null;
          try {
            /*
             * Inject stockwave script when the feature flag is enabled, the url domain
             * is an external or the stockwave debug flag is truthy
             */
            stockwaveDevMode = window?.localStorage
              ? window.localStorage.getItem('stockwave-dev-mode')
              : false;

            if (this.enableStockwaveIntegration
                && ((isInIframe() && isWithinStockwave()) || stockwaveDevMode)
            ) {
              // Need to disable the linting so we can dynamically inject our integration code - ETC
              if (!['production'].includes(import.meta.env.VITE_ENV_NAME)) {
                await import('@/vendors/Stockwave/integration-test.cjs');
              } else {
                await import('@/vendors/Stockwave/integration.cjs');
              }
              nextTick(() => {
                window.stockwave?.initialize();
              });
            } else if (oldVal && this.isAuthorized) {
              window.location.reload();
            }
          } catch (e) {
            this.noticeError('STOCKWAVE ERROR - Failed to load Stockwave script', {
              iFrameParentDocumentUrl: iFrameParentDocumentUrl(),
              validToLoadOnStockwave: isWithinStockwave(),
              isInIframe: isInIframe(),
              stockwaveDevMode,
              errorMessage: e.message,
            });
          }
        }
      },
    },
    clockDrifted() {
      if (this.clockDrifted) {
        this.track(this.eventNames.clockDrift, {
          offset: this.clockOffset,
        });
      }
    },
  },
  async created() {
    const navigationType = performance.getEntriesByType('navigation')[0]?.type ?? 'unknown';
    this.trackDatadog('page_loaded', { navigationType });

    if (this.isAuthorized) {
      await this.refreshTokens();
      this.initializeApplication();

      // Initialize Salesforce Enhanced Chat on initial load
      nextTick(() => {
        useSalesforceEnhancedChat(this.$route);
      });
    }

    eventBus.$on('acv::setBreadcrumbBackRoute', this.setBreadcrumbBackRoute);
    eventBus.$on('acv::showToastMessage', this.showToastMessage);
    eventBus.$on('acv::clearToastMessageById', this.clearToastMessageById);
  },
  beforeUnmount() {
    eventBus.$off('acv::setBreadcrumbBackRoute', this.setBreadcrumbBackRoute);
    eventBus.$off('acv::showToastMessage', this.showToastMessage);
    eventBus.$off('acv::clearToastMessageById', this.clearToastMessageById);
    this.$store.dispatch('marketplaceRouter/resetMarketplaceParams', { persistAfterLeave: true });
  },
  unmounted() {
    this.clearLaunchMessageInterval();
    this.clearPendoReadyInterval();
    localStorage.removeItem('stockwave-dev-mode');
  },
  methods: {
    ...mapActions([
      START_IDLE_TIMER,
      CLEAR_IDLE_TIMER,
    ]),
    ...mapActions({
      refreshJwtTokens: 'refreshJwtTokens',
      preloadCapitalOpenInvoices: 'capital/preloadOpenInvoices',
      setTosSuccessToastShown: 'setTosSuccessToastShown',
      checkIfNeedTosAcceptance: 'checkIfNeedTosAcceptance',
      clearTosInfo: 'clearTosInfo',
      getVdpVersionPreference: 'pandaMfe/getVdpVersionPreference',
    }),
    async refreshTokens() {
      if (!isLocked()) {
        try {
          await this.refreshJwtTokens();
        } catch (err) {
          // do nothing rn
        }
      }
    },
    async handleVisibilityChange(visible) {
      if (visible) {
        if (!window.acvNative) {
          await this.refreshTokens();
        }

        this[CLEAR_IDLE_TIMER]();
      } else {
        this[START_IDLE_TIMER]();
      }
    },
    addTermsOfServiceAcceptedSuccessfullyMessage() {
      this.showToastMessage({
        id: this.termsOfServiceAcceptedId,
        header: 'Terms of Service accepted successfully',
        severity: 'success',
        message: 'Please contact ACV at 1-800-552-4070 if you have any questions.',
        dismissible: true,
      });
      this.clearTosInfo();
    },
    addTroublePullingTermsMessage() {
      this.showToastMessage({
        id: this.troublePullingTermsId,
        header: 'Unable to process Terms of Service',
        severity: 'error',
        message: 'Please contact ACV at 1-800-552-4070 if this issue persists.',
        dismissible: true,
      });
    },
    addInactiveDealerWarning() {
      this.showToastMessage(this.buildInactiveDealerWarningToast(this.currentUser));
    },
    syncInactiveDealerWarning() {
      const action = this.resolveInactiveDealerWarningSync({
        routeName: this.$route.name,
        isLimitedUser: this.isLimitedUser,
        hasAccess: this.hasAccess,
        excludeAnnouncementsFromPage: this.excludeAnnouncementsFromPage,
        toastMessages: this.toastMessages,
      });
      if (action === 'show') {
        this.addInactiveDealerWarning();
      } else if (action === 'clear') {
        this.clearToastMessageById(this.inactiveDealerWarningId);
      }
    },
    addDealerActivatedMessage() {
      this.showToastMessage({
        id: this.dealerActivatedMessageId,
        header: 'Dealer Activated',
        severity: 'success',
        message: 'Your dealer has been activated!',
        dismissible: true,
      });
    },
    async initializeApplication() {
      this.initializePendo();

      // if this is a mobile native view, do not initialize pubnub or firebase
      if (!window.acvNative) {
        await this.$store.dispatch('initMessaging');
        await this.$store.dispatch('initFirebase');
      }

      if (this.featureFlags[this.featureFlagSlugs.datadogSessionReplay]) {
        datadogRum.startSessionReplayRecording();
      }

      await this.getVdpVersionPreference(); // get user preference for vdp version
      this.$store.dispatch('checkUserIsLimited');
      await this.$store.dispatch('setIsCapitalUser');
      await this.$store.dispatch('checkEligibilityToRewardsProgram');

      await this.checkIfNeedTosAcceptance();

      if (!this.appLaunchMessage) {
        await this.fetchLaunchMessage();

        // if there is a result, show the app launch message else hide them for now
        if (this.appLaunchMessage) {
          this.handleAppLaunchMessageVisibility(true);
        } else {
          this.handleAppLaunchMessageVisibility(false);
        }
      }

      this.preInitCapital();

      this.trackAppOpened();
    },
    initializePendo() {
      if (typeof pendo === 'undefined') return;

      pendo.initialize(this.pendoInitData);

      // poll for pendo to be ready, just in case events are triggered
      // before pendo is ready
      this.pendoReadyInterval = setInterval(() => {
        if (pendo.isReady?.()) {
          this.clearPendoReadyInterval();
          markPendoReady();
        }
      }, 50);
    },
    clearPendoReadyInterval() {
      clearInterval(this.pendoReadyInterval);
      this.pendoReadyInterval = null;
    },
    preInitCapital() {
      if (this.$route.path.includes('/capital/')) { return; }
      if (!this.currentUser.isCapitalUser) { return; }
      if (!this.currentUser.capitalUser?.dealer_selection_list) { return; }

      const [firstDealer] = this.currentUser.capitalUser.dealer_selection_list;
      let sessionDealer;
      if (window.sessionStorage) {
        sessionDealer = JSON.parse(window.sessionStorage.getItem('capital-portal'))?.dealer;
      }
      if (!firstDealer && !sessionDealer) return;

      // preload open invoices for capital users
      this.preloadCapitalOpenInvoices(sessionDealer?.id ?? firstDealer?.dealer_id);
    },
    async clearAppLaunchMessage() {
      // clear out the launch message and fetch any new messages
      // if there are no more messages, no longer show app launch messages
      await this.$store.dispatch('clearAppLaunchMessage');
      await this.fetchLaunchMessage();
      if (!this.appLaunchMessage) {
        this.handleAppLaunchMessageVisibility(false);
      }
    },
    async fetchLaunchMessage() {
      // fetch app launch messages and set a polling interval for every hour
      this.clearLaunchMessageInterval();

      if (!this.appLaunchMessage) {
        await this.$store.dispatch('getAppLaunchMessage');
      }

      this.launchMessageInterval = setInterval(() => {
        this.fetchLaunchMessage();
      }, 360000);
    },
    handleAppLaunchMessageVisibility(state) {
      // sets visibility of the app launch message
      this.canShowLaunchMessage = state;
    },
    clearLaunchMessageInterval() {
      clearInterval(this.launchMessageInterval);
      this.launchMessageInterval = null;
    },
    clearError() {
      this.$store.dispatch('auctions/setError', '');
    },
    showToastMessage(newToastMessage) {
      const toastMessageId = newToastMessage.id;
      const toastMessageIndex = this.toastMessages
        .findIndex((toastMessage) => toastMessage.id === toastMessageId);
      if (toastMessageIndex > -1) { // prevent duplicate toast messages
        this.clearToastMessageById(toastMessageId);
        this.pendingToastReinsertTimers[toastMessageId] = setTimeout(() => {
          delete this.pendingToastReinsertTimers[toastMessageId];
          this.toastMessages.push(newToastMessage);
        }, 250);
      } else {
        this.toastMessages.push(newToastMessage);
      }
    },
    setBreadcrumbBackRoute(breadcrumbRoute) {
      this.breadcrumbBackRoute = breadcrumbRoute;
    },
    clearToastMessageById(toastMessageId) {
      if (this.pendingToastReinsertTimers[toastMessageId]) {
        clearTimeout(this.pendingToastReinsertTimers[toastMessageId]);
        delete this.pendingToastReinsertTimers[toastMessageId];
      }
      const toastMessageIndex = this.toastMessages
        .findIndex((toastMessage) => toastMessage.id === toastMessageId);
      if (toastMessageIndex > -1) {
        this.toastMessages.splice(toastMessageIndex, 1);
      }
    },
    handleToastMessageLinkClicked($event) {
      $event.$event.preventDefault();
      const { toastMessage: { id: toastMessageId } } = $event;

      eventBus.$emit('acv::toastMessageLinkClicked', toastMessageId, $event.toastMessage);
    },
    trackAppOpened() {
      this.track(this.eventNames.opened, {
        cold_start: true,
        os_level_notification_enabled: get(window, 'Notification.permission') === 'granted',
      }, { withPendo: true, withMixpanel: false });
    },
  },
});
</script>

<style lang="scss" scoped>
#app {
  background: #f2f2f2;
  min-height: 100vh;
  height: 100%;
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;

  .all-headers-wrapper {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    z-index: 6;

    &.white-bg {
      @apply tw-bg-white;
    }

    .acv-banner {
      border-radius: 0;

      strong {
        font-weight: 500;
      }

      &.has-button {
        justify-content: space-between;
      }
    }

    &:has(~ .offboarding-container) .mobile-announcements {
      display: none;
    }
  }

  &.white-bg {
    @apply tw-bg-white;
  }

  .half-white {
    width: 100vw;
    height: 100vh;
    position: fixed;
    z-index: 0;

    @screen tw-lg {
      width: 51vw;
    }
  }

  &.extra-light {
    @apply tw-bg-background-light;
  }

  &.light {
    @apply tw-bg-white;
  }

  ::v-deep .acv-toast-message-list-container {
    z-index: 999999999;

    &.toast-message-list {
      bottom: 2.75rem;
      right: 0.5rem;

      @screen tw-md {
        right: 1rem;
      }

      @screen tw-lg {
        bottom: 2.5rem;
      }

      @screen tw-xl {
        right: 1.5rem;
      }
    }
  }

  .default-view:not(.simplified-view) {
    --acv-web-header-size-dynamic: var(--acv-web-header-size-mobile);

    padding-top: var(--acv-web-header-size-dynamic);
    min-height: calc(100vh - var(--acv-web-header-size-dynamic));

    &.mobile-view {
      &__tablet {
        --acv-web-header-size-dynamic: var(--acv-web-header-size-tablet);
      }

      &__desktop {
        --acv-web-header-size-dynamic: var(--acv-web-header-size-desktop);
      }
    }

    &.offboarding-container {
      min-height: 100vh;
      padding-bottom: 5rem;
    }

    // announcements push down the header area
    &.with-announcements {
      --acv-web-header-size-dynamic: var(--acv-web-header-with-announcements-mobile);

      padding-top: var(--acv-web-header-size-dynamic);
      min-height: calc(100vh - var(--acv-web-header-size-dynamic));

      &.capital-portal {
        --acv-web-header-size-dynamic: calc(var(--acv-web-header-with-announcements-mobile) + 15px);

        padding-top: calc(var(--acv-web-header-size-dynamic) + 15px);
      }

      &.mobile-view {
        &__tablet {
          --acv-web-header-size-dynamic: var(--acv-web-header-with-announcements-tablet);
        }

        &__desktop {
          --acv-web-header-size-dynamic: var(--acv-web-header-with-announcements-desktop);

          :deep(.transport-header) {
            padding-top: 50px;
          }

          &.capital-portal {
            --acv-web-header-size-dynamic: 157px;

            padding-top: 157px;
          }
        }
      }
    }

    // more specificity
    &.marketplace-view.marketplace-view {
      padding: unset;
      min-height: unset;
    }
  }

  .authentication-view {
    --acv-web-header-size-dynamic: var(--acv-web-header-size-mobile);

    padding-top: 73px;
    min-height: calc(100vh - 113px);

    @screen tw-md {
      min-height: calc(100vh - 4.4rem);
    }

    @media screen and (height <= 780px) {
      min-height: 575px;
    }
  }

  .simplified-view {
    --acv-web-header-size-dynamic: var(--acv-web-header-size-desktop);

    padding-top: 58px;
    min-height: calc(100vh - 58px);
  }

  #acvFooter {
    z-index: 2;
  }

  #acvMobileNav {
    z-index: 6;
  }
}

.rewards-dialog {
  :deep(.acv-dialog-content__body) {
    padding-bottom: unset;
  }

  :deep(.acv-dialog-content) {
    border-radius: 1rem;
  }
}
</style>

<style lang="scss">
@use "@/scss/acv";
@use '@/scss/search';
</style>
